import React from "react"
import Back from "../common/back/Back"
import Awrapper from "./Awrapper"
import "./about.css"

const pillars = [
  {
    icon: "fa fa-book-open",
    title: "Learn",
    color: "#c9a227",
    desc: "Structured, beginner-friendly curriculum delivered by experienced practitioners. No jargon, no hype — just practical knowledge.",
  },
  {
    icon: "fa fa-chart-line",
    title: "Trade",
    color: "#2e7d52",
    desc: "Hands-on exposure through live market analysis, simulations, and mentorship sessions. You trade real charts, not theory.",
  },
  {
    icon: "fa fa-seedling",
    title: "Grow",
    color: "#1a6b9a",
    desc: "Continuous development of trading psychology, risk discipline, and wealth-building habits that last a lifetime.",
  },
]

const coreValues = [
  { icon: "fa fa-shield-alt",    value: "Integrity",            meaning: "We teach only what works; we do not promise unrealistic returns." },
  { icon: "fa fa-balance-scale", value: "Financial Discipline",  meaning: "Risk management and capital preservation come before profits." },
  { icon: "fa fa-lightbulb",     value: "Innovation",           meaning: "We continuously update curricula and tools to match evolving markets." },
  { icon: "fa fa-star",          value: "Excellence",           meaning: "Our training materials, mentorship, and support meet global standards." },
  { icon: "fa fa-eye",           value: "Transparency",         meaning: "Students understand exactly what trading involves — including risks." },
  { icon: "fa fa-infinity",      value: "Continuous Learning",  meaning: "Markets change; so do we. Lifelong education is our culture." },
  { icon: "fa fa-trophy",        value: "Client Success",       meaning: "A student who trades responsibly is our greatest endorsement." },
]

const audience = [
  { icon: "fa fa-user-graduate",  label: "University Students",     desc: "Early exposure to financial markets." },
  { icon: "fa fa-briefcase",      label: "Young Professionals",     desc: "Alternate income and wealth-building skills." },
  { icon: "fa fa-store",          label: "Entrepreneurs",           desc: "Understanding cash flow and investment risk." },
  { icon: "fa fa-chart-bar",      label: "Aspiring Traders",        desc: "Structured path from beginner to consistent." },
  { icon: "fa fa-piggy-bank",     label: "Investors",               desc: "Better market awareness for portfolio decisions." },
  { icon: "fa fa-building",       label: "Corporate Teams",         desc: "Financial literacy workshops for employees." },
  { icon: "fa fa-users",          label: "Financial Literacy Seekers", desc: "No prior experience needed." },
]

const About = () => {
  return (
    <>
      <Back title="About Us" />

      {/* ── 1. Intro ───────────────────────────── */}
      <section className="about-intro padding">
        <div className="container">
          <div className="about-intro-grid">
            <div className="about-intro-img">
              <img src="./images/about.webp" alt="Rovers Access Limited trading education" />
              <div className="about-motto-badge">
                <i className="fa fa-quote-left"></i>
                <span>GAMBLING IS NOT TRADING.</span>
              </div>
            </div>
            <div className="about-intro-text">
              <p className="about-eyebrow">WELCOME TO ROVERS ACCESS LIMITED</p>
              <h1 className="about-headline">The Pulse of Trading in the Financial Markets</h1>
              <div className="about-divider"></div>
              <p>
                Headquartered in <strong>Nairobi, Kenya</strong>, our second-to-none competitive
                practical and technology-based programme provides us with an edge over others — a pride
                that enables us to maintain leadership in the market.
              </p>
              <p>
                Our pride emanates from the practical trading knowledge designed to provide our clients
                the skills needed to prepare them to <strong>confidently trade in the Financial Markets</strong>.
                Our small-class setup enables one-on-one instructor engagement and attention to detail.
              </p>
              <p>
                Our Core Programme captures the essential rules and skills needed for trading
                <strong> Forex, Futures and Options</strong>. Whether you are an hourly, daily, weekly,
                monthly or swing trader — the Core Programme is our signature course.
              </p>
              <p>
                Rovers Access avoids Social Media hype and instead focuses on a knowledge-based curriculum
                that suits the needs of all traders. Our students are trained to turn knowledge into skills
                that befit the tactics needed to be a <strong>confident, disciplined trader</strong> — a
                programme that is a world away from gambling or novice trading.
              </p>
              <div className="about-highlights">
                <div className="highlight-item">
                  <i className="fa fa-check-circle"></i>
                  <span>Small class sizes — one-on-one attention</span>
                </div>
                <div className="highlight-item">
                  <i className="fa fa-check-circle"></i>
                  <span>Live charts, real risk, real markets</span>
                </div>
                <div className="highlight-item">
                  <i className="fa fa-check-circle"></i>
                  <span>Forex, Futures, Options, Indices & CFDs</span>
                </div>
                <div className="highlight-item">
                  <i className="fa fa-check-circle"></i>
                  <span>Knowledge-based — zero social media hype</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2. Stats band ─────────────────────── */}
      <Awrapper />

      {/* ── 3. Three pillars ──────────────────── */}
      <section className="about-pillars padding">
        <div className="container">
          <div className="about-section-header">
            <p className="about-eyebrow">OUR METHODOLOGY</p>
            <h2>Three Pillars of Everything We Do</h2>
            <p className="about-section-sub">
              By combining modern learning platforms, real-time market insights, and personalised
              mentorship, Rovers Access transforms theoretical curiosity into confident, informed
              market participation.
            </p>
          </div>
          <div className="pillars-grid">
            {pillars.map((p, i) => (
              <div className="pillar-card" key={i} style={{ "--pillar-color": p.color }}>
                <div className="pillar-icon">
                  <i className={p.icon}></i>
                </div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. Mission & Vision ───────────────── */}
      <section className="about-mv padding">
        <div className="container">
          <div className="mv-grid">
            <div className="mv-card mission">
              <div className="mv-icon"><i className="fa fa-bullseye"></i></div>
              <h3>Our Mission</h3>
              <p>
                To empower individuals across Africa with world-class financial market education,
                practical trading skills, and strategic mentorship that enables long-term financial
                growth, risk-aware decision making, and genuine independence from single-income
                dependency.
              </p>
              <blockquote>
                We measure our success not by how many courses we sell, but by how many students
                trade responsibly, manage losses gracefully, and grow consistently.
              </blockquote>
            </div>
            <div className="mv-card vision">
              <div className="mv-icon"><i className="fa fa-eye"></i></div>
              <h3>Our Vision</h3>
              <p>
                To become <strong>Africa's leading hub</strong> for financial literacy, forex trading
                education, and digital investment training — recognised for producing disciplined,
                profitable traders and for raising the standard of financial knowledge across the
                continent.
              </p>
              <div className="mv-motto">
                <span>Learn</span>
                <i className="fa fa-circle"></i>
                <span>Trade</span>
                <i className="fa fa-circle"></i>
                <span>Grow</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. Core Values ────────────────────── */}
      <section className="about-values padding">
        <div className="container">
          <div className="about-section-header">
            <p className="about-eyebrow">WHAT WE STAND FOR</p>
            <h2>Core Values</h2>
          </div>
          <div className="values-grid">
            {coreValues.map((v, i) => (
              <div className="value-card" key={i}>
                <div className="value-icon"><i className={v.icon}></i></div>
                <h4>{v.value}</h4>
                <p>{v.meaning}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. Who We Serve ───────────────────── */}
      <section className="about-audience padding">
        <div className="container">
          <div className="about-section-header">
            <p className="about-eyebrow">WHO WE SERVE</p>
            <h2>Our Target Clients</h2>
            <p className="about-section-sub">
              Rovers Access serves a diverse audience — from first-time market participants to
              experienced professionals seeking structured, practical trading knowledge.
            </p>
          </div>
          <div className="audience-grid">
            {audience.map((a, i) => (
              <div className="audience-card" key={i}>
                <div className="audience-icon"><i className={a.icon}></i></div>
                <h4>{a.label}</h4>
                <p>{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

export default About
